import MwCompatibilityCheckerPlugin from '@highq/mw-compatibility-checker'
import configinsert from '@plugins/configinsert'
import { initI18n } from '@plugins/i18n'
import { connectKeycloak } from '@plugins/keycloak.js'
import toastPlugin, { POSITION, ToastOptions } from '@plugins/toast/index.js'
import TowsSessionHandler from '@plugins/towsSessionHandler'
import utility from '@plugins/util.js'
import axios from 'axios'
import { createApp, reactive } from 'vue'
import packageJson from '../package.json'
import App from './App.vue'
import { keycloakState } from './lib/state'
import router from './router'

// allow for a bigger timeout, since the tows does not always
// respond under 20s (default timeout)
axios.defaults.timeout = 60000
// configure app
const app = createApp(App)

app.use(MwCompatibilityCheckerPlugin, {
    versionEndpointUrl: utility.middleware() + '/version',
    compatibleSemverVersion: packageJson.compatibleMiddlewareVersion
})

app.use(router)
app.provide('util', utility)

// provide an empty session and keycloak to the app, so we can mount
// the app and show loading stuff before the sessions are established
const reactiveSession           = reactive({ available: false })
const reactiveKeycloak          = keycloakState
const reactiveMiddlewarePortalFunctions = reactive({ available: false })
const reactiveBaseData          = reactive({ available: false })
const reactiveSystemConfig      = reactive({ available: false })
const reactiveClientLogo        = reactive({ available: false })
// global state for various things
const reactiveGlobalState  = reactive({
    isLoading:                  false,
    customerNr:                 undefined,
    subscriptionLinkingOnly:    true
})

app.provide('session',      reactiveSession)
app.provide('keycloak',     reactiveKeycloak)
app.provide('middlewarePortalFunctions',     reactiveMiddlewarePortalFunctions)
app.provide('baseData',     reactiveBaseData)
app.provide('systemConfig', reactiveSystemConfig)
app.provide('globalState',  reactiveGlobalState)
app.provide('clientLogo',   reactiveClientLogo)


// initialize a tows connection and establish a session
const client = (new URL(window.location)).searchParams.get('Mandant') || (new URL(window.location)).searchParams.get('mandant') || (new URL(window.location)).searchParams.get('client')
const ktnr   = (new URL(window.location)).searchParams.get('KTNR')    || (new URL(window.location)).searchParams.get('ktnr')    || (new URL(window.location)).searchParams.get('corporation')

const towsSessionHandler = new TowsSessionHandler(client, ktnr, utility.middleware())

initCriticalData('aboonline-portal', client)
.then(({ keycloakInstance, middlewarePortalFunctions, userToken, toastOptions, i18n, requiredConfig}) => {
    app.use(toastPlugin, toastOptions)
    app.use(i18n)
    app.use(configinsert, {requiredFields: requiredConfig})

    reactiveKeycloak.available = true
    reactiveMiddlewarePortalFunctions.available = true
    reactiveKeycloak.npsUserToken = userToken
    Object.assign(reactiveKeycloak, keycloakInstance)
    Object.assign(reactiveMiddlewarePortalFunctions, middlewarePortalFunctions)

    // the app gets mounted when most basic stuff is ready
    // // ### --- APP MOUNTING --- ###
    // app.mount('#app')
    // // ### --- --- --- --- --- ###
})
.then(() => {
    // get the tows session next
    return towsSessionHandler.connect()
})
.then(session => {
    // make the session available to the app
    session.available = true
    Object.assign(reactiveSession, session)

    // NOW WE ARE FREE TO LOAD THINGS ASYNC AND MAKING THEM AVAILABLE TO THE APP

    // break from the promise chain and do everything in parallel
    // get the necessary baseData
    towsSessionHandler.getBaseData(session.clientId)
    .then(baseData => {
        reactiveBaseData.available = true
        Object.assign(reactiveBaseData, baseData)
    })

    // next async call
    towsSessionHandler.getSystemConfiguration(session.clientId)
    .then(config => {
        reactiveSystemConfig.available = true
        Object.assign(reactiveSystemConfig, config)
    })

    // next async call
    towsSessionHandler.getClientLogo(session.clientId)
    .then(logo => {
        reactiveClientLogo.available = true
        Object.assign(reactiveClientLogo, logo)
    })    
})
.then(() => app.mount('#app'))

async function initCriticalData(product, clientName) {
    const result = {
        keycloakInstance: undefined,
        middlewarePortalFunctions: {
            roles: undefined
        },
        userToken: undefined,
        toastOptions: undefined,
        i18n: undefined,
        requiredConfig: undefined
    }

    // ---- ---- KEYCLOAK CONNECTION ---- ---- //
    const configResponse = await axios.get(utility.middleware() + '/config')

    if (configResponse.data.keycloak.clientPerToClient) {
        configResponse.data.keycloak.clientId += '-' + client
    }

    const keycloakInstance = await connectKeycloak(app, configResponse.data.keycloak)

    result.keycloakInstance = keycloakInstance
    // ---- ---- ---- ---- ---- ---- ---- ---- //

    // ---- ---- AXIOS HEADER CONFIGURATION ---- ---- //
    // configure axios to use the session id and token as default header
    axios.interceptors.request.use(config => {
        // set sessionid as header in every request
        // addtionally set the rest token for compatibility
        config.headers['hq-token']     = reactiveSession.id
        config.headers['hq-jwt-token'] = reactiveSession.token

        config.headers['Authorization'] = 'Bearer ' + keycloakInstance.token

        return config
    })
    // ---- ---- ---- ---- ---- ---- ---- ---- //

    if(configResponse.data.portalFunctions) {
        result.middlewarePortalFunctions.roles = configResponse.data.portalFunctions
    }

    // ---- ---- GET/CREATE/LINK NPS USER ---- ---- //
    // get or create and link the user
    // -> customer creates standalone kc login and wants to add his subscriptions
    // -> this will check if the user exists and if the account can be linked
    // ->
    let getOrCreateUserUrl = utility.middleware()
    getOrCreateUserUrl += '/abo-online/external-users/' + keycloakInstance.tokenParsed.sub

    const getOrCreateUserBody = {
        email: keycloakInstance.tokenParsed.email
    }

    const userResponse = await axios.patch(getOrCreateUserUrl, getOrCreateUserBody)
    result.userToken = userResponse.data.userToken
    // ---- ---- ---- ---- ---- ---- ---- ---- //

    // ---- ---- INIT TOAST PLUGIN --- --- //
    const toastOptions = new ToastOptions()
    toastOptions.position = POSITION.TOP_MID
    result.toastOptions = toastOptions
    // ---- ---- ---- ---- ---- ---- ---- ---- //

    // ---- ---- GET LOCALE OVERRIDES ---- ---- //
    const overridesResponse = await axios.get(utility.middleware() + `/locales/${product}/${clientName}`)
    result.i18n = initI18n(overridesResponse.data)
    // ---- ---- ---- ---- ---- ---- ---- ---- //


    // ---- ---- GET REQUIRED FIELDS ---- ---- //
    const requiredFieldsRespons = await axios.get(utility.middleware() + `/configs/${product}/${clientName}/required-fields`)
    result.requiredConfig = requiredFieldsRespons.data
    // ---- ---- ---- ---- ---- ---- ---- ---- //

    return result
}